import React, { useState, useEffect } from 'react';
import { Select, Spin, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { getDashboardStatistics, DashboardMonthStat, getPagePerformance, PagePerformanceData, getTelesalePerformance, TelesalePerformanceData, getTelesaleStatistics, TelesaleMonthStat, getSaleBranchPerformance, SaleBranchPerformanceData, getSaleBranchStatistics, SaleBranchStatisticsData } from '../../api/DashboardStatistics';
import { getAppointments } from '../../api/Appointment';
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, Tooltip as RechartsTooltip,
} from 'recharts';
import {
    TrendingUp, DollarSign, Users, Target, Zap, ArrowUpRight, ArrowDownRight,
    MessageCircle, BarChart3, User, Calendar, Briefcase, Activity, AlertTriangle,
    PhoneCall, PhoneMissed, PhoneOff, UserX, Trophy, AlertCircle, Phone,
    Stethoscope, CalendarCheck, Award, CalendarDays, Clock, CheckCircle2, XCircle, MessageSquare, BarChart2
} from 'lucide-react';
import { useTranslation } from "react-i18next";

import Layout from '../../Layout'


// ================= MOCK DATA =================

// 1. Dữ liệu Tổng quan
const overviewData = [
    { name: 'Tuần 1', revenue: 400, target: 350, leads: 1200 },
    { name: 'Tuần 2', revenue: 550, target: 400, leads: 1500 },
    { name: 'Tuần 3', revenue: 480, target: 450, leads: 1300 },
    { name: 'Tuần 4', revenue: 690, target: 500, leads: 1800 },
];
const systemFunnel = [
    { stage: 'Data từ Page', value: 5800 },
    { stage: 'Telesale tiếp cận', value: 4350 },
    { stage: 'Khách đến cơ sở', value: 1300 },
    { stage: 'Khách xác nhận làm', value: 780 },
];

// 2. Dữ liệu Trực Page
const callResultChartData = [
    { name: 'Thành công', value: 2450, color: '#10b981' },
    { name: 'Sai số', value: 850, color: '#ef4444' },
    { name: 'Từ chối', value: 650, color: '#f59e0b' },
    { name: 'Không liên lạc được', value: 400, color: '#94a3b8' },
];
const topPageStaffData = [
    { name: 'Nguyễn Văn A', messages: 4500, leads: 320 },
    { name: 'Trần Thị B', messages: 4100, leads: 290 },
    { name: 'Lê Văn C', messages: 3800, leads: 310 },
    { name: 'Phạm D', messages: 3500, leads: 250 },
    { name: 'Hoàng E', messages: 3200, leads: 210 },
];
const pageFunnelStages = [
    { label: "Khách hàng mới", amount: "35,000", color: "#e11d48", width: 100, nextWidth: 80 },
    { label: "Tò mò", amount: "22,500", color: "#9333ea", width: 80, nextWidth: 65 },
    { label: "Nhận thức kém", amount: "15,200", color: "#3b82f6", width: 65, nextWidth: 45 },
    { label: "Tìm hiểu sơ bộ", amount: "8,400", color: "#f59e0b", width: 45, nextWidth: 30 },
    { label: "Nhu cầu rõ nhưng rào cản", amount: "4,500", color: "#10b981", width: 30, nextWidth: 15 },
    { label: "Nhu cầu cao", amount: "2,700", color: "#dc2626", width: 15, nextWidth: 15 },
];

// 3. Dữ liệu Telesale
const tsRevenueTrendData = [
    { name: 'T1', current: 320, target: 300 },
    { name: 'T2', current: 280, target: 300 },
    { name: 'T3', current: 410, target: 400 },
    { name: 'T4', current: 450, target: 400 },
    { name: 'T5', current: 480, target: 450 },
    { name: 'T6', current: 520, target: 500 },
    { name: 'T7', current: 490, target: 500 },
    { name: 'T8', current: 550, target: 550 },
    { name: 'T9', current: 610, target: 600 },
    { name: 'T10', current: 680, target: 650 },
    { name: 'T11', current: null, target: 700 },
    { name: 'T12', current: null, target: 750 },
];

const telesaleFunnelStages = [
    {
        label: "Nhu cầu cao, cân nhắc lựa chọn", amount: "5,800", color: "#e11d48", width: 100, nextWidth: 80
    },
    { label: "Đã đặt lịch", amount: "5,500", color: "#9333ea", width: 80, nextWidth: 65 },
    { label: "Đã xác nhận / khách đã đến", amount: "4,350", color: "#3b82f6", width: 65, nextWidth: 45 },
    { label: "Xác nhận làm", amount: "1,520", color: "#f59e0b", width: 45, nextWidth: 30 },
    { label: "Khách đã đặt lịch chưa tới", amount: "910", color: "#10b981", width: 30, nextWidth: 15 },
    { label: "Trạng thái rớt", amount: "182", color: "#dc2626", width: 15, nextWidth: 15 },
];

const tsServiceChartData = [
    { name: 'Điều trị Da', reached: 1250, booked: 450 },
    { name: 'Phẫu thuật TM', reached: 850, booked: 210 },
    { name: 'Nha khoa', reached: 1050, booked: 320 },
    { name: 'Giảm béo', reached: 780, booked: 180 },
    { name: 'Phun xăm', reached: 920, booked: 360 },
];

const tsEmployeeData = [
    { name: 'Trần Thị B', assigned: 800, called: 780, reached: 640, booked: 256, showed: 166, closed: 42, revenue: 450 },
    { name: 'Hoàng Văn E', assigned: 750, called: 700, reached: 525, booked: 184, showed: 110, closed: 24, revenue: 290 },
    { name: 'Lê Kiên', assigned: 850, called: 840, reached: 714, booked: 271, showed: 157, closed: 31, revenue: 350 },
    { name: 'Phạm Hương', assigned: 600, called: 590, reached: 413, booked: 124, showed: 62, closed: 9, revenue: 110 },
    { name: 'Đỗ Quyên', assigned: 700, called: 650, reached: 442, booked: 110, showed: 50, closed: 6, revenue: 85 },
    { name: 'Nguyễn Tùng', assigned: 650, called: 620, reached: 480, booked: 150, showed: 90, closed: 15, revenue: 190 },
    { name: 'Cao Tâm', assigned: 500, called: 490, reached: 380, booked: 130, showed: 80, closed: 12, revenue: 160 },
    { name: 'Vũ Hằng', assigned: 550, called: 530, reached: 400, booked: 140, showed: 75, closed: 10, revenue: 140 },
    { name: 'Lý Quốc', assigned: 900, called: 880, reached: 750, booked: 280, showed: 170, closed: 35, revenue: 410 },
    { name: 'Đinh Lan', assigned: 680, called: 660, reached: 510, booked: 170, showed: 100, closed: 18, revenue: 220 },
];

// 4. Dữ liệu Sale Cơ Sở & Khác
const chartTooltipStyle = { borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' };

const financialDataYearly = [
    { month: 'T1', revenue: 1100, messages: 12500, orders: 1320 },
    { month: 'T2', revenue: 950, messages: 11200, orders: 2280 },
    { month: 'T3', revenue: 1250, messages: 14000, orders: 3350 },
    { month: 'T4', revenue: 1350, messages: 15500, orders: 4370 },
    { month: 'T5', revenue: 1400, messages: 16200, orders: 5380 },
    { month: 'T6', revenue: 1600, messages: 18000, orders: 16400 },
    { month: 'T7', revenue: 1800, messages: 21000, orders: 17420 },
    { month: 'T8', revenue: 2100, messages: 23000, orders: 18450 },
    { month: 'T9', revenue: 2350, messages: 25000, orders: 19480 },
    { month: 'T10', revenue: 2600, messages: 28000, orders: 20500 },
    { month: 'T11', revenue: 2800, messages: 31000, orders: 29520 },
    { month: 'T12', revenue: 3200, messages: 35000, orders: 29550 },
];

const orderStatusData = [
    { name: 'T1', newOrders: 420, successfulOrders: 310, cancelledOrders: 45 },
    { name: 'T2', newOrders: 380, successfulOrders: 280, cancelledOrders: 35 },
    { name: 'T3', newOrders: 510, successfulOrders: 390, cancelledOrders: 60 },
    { name: 'T4', newOrders: 560, successfulOrders: 450, cancelledOrders: 50 },
    { name: 'T5', newOrders: 600, successfulOrders: 480, cancelledOrders: 65 },
    { name: 'T6', newOrders: 650, successfulOrders: 520, cancelledOrders: 70 },
    { name: 'T7', newOrders: 620, successfulOrders: 500, cancelledOrders: 60 },
    { name: 'T8', newOrders: 680, successfulOrders: 550, cancelledOrders: 55 },
    { name: 'T9', newOrders: 750, successfulOrders: 610, cancelledOrders: 65 },
    { name: 'T10', newOrders: 820, successfulOrders: 680, cancelledOrders: 75 },
    { name: 'T11', newOrders: 0, successfulOrders: 0, cancelledOrders: 0 },
    { name: 'T12', newOrders: 0, successfulOrders: 0, cancelledOrders: 0 },
];

const pageStatusData = [
    { name: '1. Khách hàng mới', value: 5800, rate: 100 },
    { name: '2. Tò mò nhận thức kém', value: 4200, rate: 72.4 },
    { name: '4. Tìm hiểu sơ bộ', value: 1800, rate: 31.0 },
    { name: '5. Nhu cầu rõ ràng nhưng còn rào cản', value: 950, rate: 16.3 },
    { name: '6. Nhu cầu cao, cân nhắc lựa chọn', value: 450, rate: 7.7 },
];

const fanpageSourceData = [
    { name: 'Cấy tóc Newhair', value: 3042 },
    { name: 'VTM Khơ Thị (1)', value: 2504 },
    { name: 'Hoa hậu Thu Hoài', value: 663 },
    { name: 'VTM Khơ Thị (2)', value: 469 },
    { name: 'Tài trợ thẩm mỹ', value: 400 },
    { name: 'Viện PTTM Khơ Thị', value: 360 },
    { name: 'VTM Thảo Điền', value: 357 },
    { name: 'Săn Deal Khơ Thị', value: 351 },
];

const platformRevenueData = [
    { platform: 'Facebook', face: 800, zalo: 0, tiktok: 0 },
    { platform: 'Zalo', face: 0, zalo: 500, tiktok: 0 },
    { platform: 'TikTok', face: 0, tiktok: 400, zalo: 0 },
];

const staffComparisonData = [
    { name: 'Trần T. A', chats: 1250, leads: 420 },
    { name: 'Nguyễn V. B', chats: 1100, leads: 390 },
    { name: 'Lê T. C', chats: 1050, leads: 380 },
    { name: 'Phạm H. D', chats: 980, leads: 340 },
    { name: 'Hoàng V. E', chats: 950, leads: 310 },
    { name: 'Đỗ T. F', chats: 920, leads: 290 },
    { name: 'Vũ V. G', chats: 880, leads: 270 },
    { name: 'Bùi T. H', chats: 850, leads: 250 },
    { name: 'Đặng V. K', chats: 810, leads: 240 },
    { name: 'Ngô T. L', chats: 780, leads: 220 },
];

// 4. Dữ liệu Sale Cơ Sở & Khác
const onsiteStatusData = [
    { name: 'Xác nhận làm', value: 60, color: '#10b981' },
    { name: 'Không nhu cầu', value: 25, color: '#f59e0b' },
    { name: 'Rớt do bệnh lý', value: 15, color: '#ef4444' },
];
const topSalesData = [
    { name: 'Nguyễn Văn A', revenue: 450, conversions: 85, department: 'Sale Cơ sở' },
    { name: 'Trần Thị B', revenue: 380, conversions: 92, department: 'Telesale' },
    { name: 'Lê Văn C', revenue: 350, conversions: 78, department: 'Sale Cơ sở' },
    { name: 'Phạm Thị D', revenue: 310, conversions: 65, department: 'Sale Cơ sở' },
    { name: 'Hoàng Văn E', revenue: 290, conversions: 88, department: 'Telesale' },
];

const onsiteFunnelData = [
    { stage: 'Khách đến cơ sở', value: 990 },
    { stage: 'Đã tư vấn', value: 920 },
    { stage: 'Xác nhận làm', value: 594 },
    { stage: 'Đã thanh toán', value: 550 },
];

const topSellingServicesData = [
    { name: 'Nâng mũi Cấu trúc', quantity: 125, revenue: 2500 },
    { name: 'Hút mỡ bụng', quantity: 85, revenue: 1700 },
    { name: 'Cắt mí Plasma', quantity: 320, revenue: 1120 },
    { name: 'Trị Nám Laser', quantity: 450, revenue: 950 },
    { name: 'Căng chỉ Collagen', quantity: 110, revenue: 880 },
    { name: 'Phun môi Pha lê', quantity: 520, revenue: 780 },
];

const onsiteEmployeeData = [
    { name: 'Nguyễn Văn A', consulted: 120, closed: 85, revenue: 450 },
    { name: 'Lê Văn C', consulted: 115, closed: 78, revenue: 350 },
    { name: 'Phạm Thị D', consulted: 95, closed: 65, revenue: 310 },
    { name: 'Vũ Đức E', consulted: 88, closed: 55, revenue: 280 },
    { name: 'Hoàng Anh F', consulted: 75, closed: 42, revenue: 210 },
].sort((a, b) => b.revenue - a.revenue);

const marketingMetricCards = [
    { id: 'new', label: 'Đặt lịch mới', value: '125', trend: '+15%', isPositive: true, icon: CalendarDays, color: 'bg-blue-500' },
    { id: 'confirmed', label: 'Tỷ lệ Tiếp Cận', value: '98', trend: '+5.2%', isPositive: true, icon: CalendarCheck, color: 'bg-cyan-500' },
    { id: 'pending', label: 'Tỷ lệ Đặt Lịch', value: '45', trend: '-2.1%', isPositive: false, icon: Clock, color: 'bg-orange-500' },
    { id: 'completed', label: 'Tỷ lệ Chốt Cuối', value: '320', trend: '+8.5%', isPositive: true, icon: CheckCircle2, color: 'bg-green-500' },
    { id: 'cancelled', label: 'Show-rate', value: '15', trend: '-1.2%', isPositive: false, icon: XCircle, color: 'bg-red-500' },
];

const callPerformanceData = [
    { name: 'Không nghe máy', value: 120, color: '#f59e0b' },
    { name: 'Thuê bao bận', value: 85, color: '#3b82f6' },
    { name: 'Thuê bao tắt máy', value: 45, color: '#9ca3af' },
    { name: 'Ngoài vùng phủ sóng', value: 30, color: '#8b5cf6' },
    { name: 'Từ chối cuộc gọi', value: 25, color: '#f43f5e' },
    { name: 'Sai số điện thoại', value: 15, color: '#ef4444' },
];

const zaloPerformanceData = [
    { name: 'Khách không phản hồi', value: 85, color: '#f59e0b' },
    { name: 'Khách không đồng ý kết bạn', value: 45, color: '#3b82f6' },
    { name: 'Khách chặn người lạ', value: 25, color: '#ef4444' },
    { name: 'Không tìm thấy Zalo', value: 30, color: '#9ca3af' },
    { name: 'Gọi zalo không được', value: 20, color: '#8b5cf6' },
];

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const CustomOnsiteTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-100 min-w-[220px]">
                <p className="font-bold text-slate-800 mb-3 border-b border-slate-100 pb-2">{label}</p>
                <div className="space-y-2 text-sm">
                    {payload.map((entry, index) => (
                        <div key={index} className="flex justify-between items-center gap-4">
                            <span className="flex items-center gap-2 font-medium text-slate-600">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                                {entry.name}
                            </span>
                            <span className="font-bold text-slate-800">
                                {entry.value} {entry.dataKey === 'revenue' || entry.dataKey === 'aov' ? 'Tr' : ''}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const CustomFunnelChart = ({ data }) => {
    const colors = ['#e91e63', '#8e24aa', '#3949ab', '#fb8c00', '#43a047', '#e53935'];
    const width = 500;
    const height = 300;
    const topWidth = 220;
    const bottomWidth = 60;
    const cx = 120;
    const numSections = data.length;
    const sectionHeight = height / numSections;

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full drop-shadow-sm">
            {data.map((item, i) => {
                const yTop = i * sectionHeight;
                const yBottom = (i + 1) * sectionHeight;
                const wTop = topWidth - (i * (topWidth - bottomWidth)) / numSections;
                const wBottom = topWidth - ((i + 1) * (topWidth - bottomWidth)) / numSections;
                const xTopLeft = cx - wTop / 2;
                const xTopRight = cx + wTop / 2;
                const xBottomLeft = cx - wBottom / 2;
                const xBottomRight = cx + wBottom / 2;

                const points = `${xTopLeft},${yTop} ${xTopRight},${yTop} ${xBottomRight},${yBottom} ${xBottomLeft},${yBottom}`;
                const midY = yTop + sectionHeight / 2;
                const lineStartX = (xTopRight + xBottomRight) / 2;
                const lineEndX = cx + topWidth / 2 + 25;

                return (
                    <g key={i} className="group cursor-pointer">
                        <polygon points={points} fill={item.color || colors[i % colors.length]} className="transition-all duration-300 hover:brightness-110 hover:drop-shadow-md" />
                        <polyline points={`${lineStartX},${midY} ${lineEndX},${midY}`} fill="none" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2 2" />
                        <text x={lineEndX + 8} y={midY - 8} fill="#1e293b" fontSize="13" fontWeight="bold" dominantBaseline="middle">
                            {item.value.toLocaleString()} khách
                        </text>
                        <text x={lineEndX + 8} y={midY + 12} fill="#64748b" fontSize="12" fontWeight="500" dominantBaseline="middle">
                            {item.name}
                        </text>
                        <text x={cx} y={midY} fill="#ffffff" fontSize="13" fontWeight="bold" textAnchor="middle" dominantBaseline="middle" className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                            {item.rate}%
                        </text>
                    </g>
                );
            })}
        </svg>
    );
};

// Tick trục Y cho biểu đồ kênh: tối đa 2 dòng, quá thì cắt kèm "…", hover hiện tên đầy đủ
const ChannelAxisTick = ({ x, y, payload }: any) => {
    const label = String(payload?.value ?? '');
    const MAX_CHARS = 16;
    const words = label.split(' ');
    const lines: string[] = [];
    let current = '';
    let truncated = false;
    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (candidate.length <= MAX_CHARS || !current) {
            current = candidate;
        } else {
            if (lines.length === 1) { truncated = true; break; }
            lines.push(current);
            current = word;
        }
    }
    if (!truncated && current) lines.push(current);
    if (truncated) {
        const second = current.length > MAX_CHARS - 1 ? current.slice(0, MAX_CHARS - 1) : current;
        lines.push(`${second}…`);
    }
    return (
        <g>
            <title>{label}</title>
            <text x={x} y={y} textAnchor="end" fontSize={11} fontWeight={500} fill="#64748b">
                {lines.map((line, i) => (
                    <tspan key={i} x={x} dy={i === 0 ? (lines.length > 1 ? -2 : 4) : 13}>{line}</tspan>
                ))}
            </text>
        </g>
    );
};

// ================= UI COMPONENTS =================

const StatCard = ({ title, value, subValue, icon: Icon, colorClass, trend }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between group hover:shadow-md transition-all duration-300 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/0 to-slate-50/50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform" />
        <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
            {subValue && (
                <p className={`text-sm mt-2 font-medium flex items-center gap-1 ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-rose-600' : 'text-slate-500'}`}>
                    {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : trend === 'down' ? <ArrowDownRight className="w-4 h-4" /> : null}
                    {subValue}
                </p>
            )}
        </div>
        <div className={`p-4 rounded-xl ${colorClass}`}>
            <Icon className="w-6 h-6 text-white" />
        </div>
    </div>
);

const CustomSVGFunnel = ({ data }) => {
    const FUNNEL_HEIGHT = 340;
    const SECTION_HEIGHT = FUNNEL_HEIGHT / data.length;
    const CENTER_X = 175;
    const MAX_HALF_WIDTH = 165;
    const LINE_X_END = 370;

    return (
        <div className="w-full flex justify-center py-4">
            <svg
                viewBox="0 0 700 355"
                preserveAspectRatio="xMidYMid meet"
                className="w-full h-auto"
            >
                {data.map((stage, index) => {
                    const yTop = index * SECTION_HEIGHT;
                    const yBottom = yTop + SECTION_HEIGHT - 2;
                    const topHalfWidth = (stage.width / 100) * MAX_HALF_WIDTH;
                    const bottomHalfWidth = (stage.nextWidth / 100) * MAX_HALF_WIDTH;
                    const topLeft = CENTER_X - topHalfWidth;
                    const topRight = CENTER_X + topHalfWidth;
                    const bottomLeft = CENTER_X - bottomHalfWidth;
                    const bottomRight = CENTER_X + bottomHalfWidth;
                    const pathD = `M ${topLeft} ${yTop} L ${topRight} ${yTop} L ${bottomRight} ${yBottom} L ${bottomLeft} ${yBottom} Z`;
                    const lineXStart = topRight + 10;
                    const lineXEnd = LINE_X_END;
                    const lineY = yTop + SECTION_HEIGHT / 2;

                    // Ưu tiên dùng số gốc (stage.value); nếu không có thì bóc số từ chuỗi amount
                    const toNumber = (s) => Number(String(s).replace(/[^\d]/g, '')) || 0;
                    let percentText = "";
                    if (index > 0) {
                        const prev = data[index - 1];
                        const prevAmount = prev.value !== undefined ? Number(prev.value) : toNumber(prev.amount);
                        const currAmount = stage.value !== undefined ? Number(stage.value) : toNumber(stage.amount);
                        // Tránh chia 0 -> NaN/Infinity
                        percentText = prevAmount > 0
                            ? `-${(((prevAmount - currAmount) / prevAmount) * 100).toFixed(1)}%`
                            : "—";
                    }

                    return (
                        <g key={index} className="group transition-all duration-300 hover:brightness-110 cursor-pointer">
                            <path d={pathD} fill={stage.color} className="transition-all duration-300 drop-shadow-sm group-hover:drop-shadow-md" />
                            <text x={CENTER_X} y={lineY} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize={20} fontWeight={700} className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-md">
                                {percentText}
                            </text>
                            <path d={`M ${lineXStart} ${lineY} L ${lineXEnd} ${lineY}`} stroke="#cbd5e1" strokeWidth="1" />
                            <text x={lineXEnd + 12} y={lineY - 8} fill="#334155" fontSize={17} fontWeight={600}>{stage.label}</text>
                            <text x={lineXEnd + 12} y={lineY + 16} fill="#64748b" fontSize={16} fontWeight={500}>{stage.amount}</text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// ================= MAIN APP =================

export default function App() {
    const [activeTab, setActiveTab] = useState('page');
    const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });

    const [performanceTab, setPerformanceTab] = useState('phone'); // 'phone' | 'zalo'

    const activePerformanceData = performanceTab === 'phone' ? callPerformanceData : zaloPerformanceData;
    const totalPerformance = activePerformanceData.reduce((sum, item) => sum + item.value, 0);

    // Thống kê biểu đồ theo tháng (API /dashboard/statistics)
    const currentYear = new Date().getFullYear();
    const [statsYear, setStatsYear] = useState(currentYear);
    const [statsChart, setStatsChart] = useState<DashboardMonthStat[]>([]);
    const [statsLoading, setStatsLoading] = useState(false);
    const yearOptions = Array.from({ length: 6 }, (_, i) => {
        const y = currentYear - i;
        return { label: `Năm ${y}`, value: y };
    });

    useEffect(() => {
        let ignore = false;
        setStatsLoading(true);
        getDashboardStatistics(statsYear).then((res) => {
            if (ignore) return;
            setStatsChart(res.status && res.data?.chart ? res.data.chart : []);
            setStatsLoading(false);
        });
        return () => { ignore = true; };
    }, [statsYear]);

    const statsChartData = statsChart.map((c) => ({
        month: `T${c.month}`,
        revenue: c.revenue,
        customers: c.total_new_customers,
        appointments: c.total_completed_appointments,
    }));

    // Khoảng thời gian + chi nhánh dùng chung cho toàn trang (bộ lọc ở thanh trên cùng)
    const [perfRange, setPerfRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs()]);
    const [tsBranchId, setTsBranchId] = useState<number | undefined>(undefined);

    // ===== Telesale (API /dashboard/telesale-performance-new + /dashboard/telesale-statistics-new) =====
    const [tsData, setTsData] = useState<TelesalePerformanceData | null>(null);
    const [tsLoading, setTsLoading] = useState(false);
    const [tsYear, setTsYear] = useState(new Date().getFullYear());
    const [tsChart, setTsChart] = useState<Record<string, TelesaleMonthStat>>({});
    const [tsChartLoading, setTsChartLoading] = useState(false);

    useEffect(() => {
        let ignore = false;
        setTsLoading(true);
        getTelesalePerformance(
            perfRange[0].format('YYYY-MM-DD'),
            perfRange[1].format('YYYY-MM-DD'),
            tsBranchId
        ).then((res) => {
            if (ignore) return;
            setTsData(res.status && res.data ? res.data : null);
            setTsLoading(false);
        });
        return () => { ignore = true; };
    }, [perfRange, tsBranchId]);

    useEffect(() => {
        let ignore = false;
        setTsChartLoading(true);
        getTelesaleStatistics(tsYear).then((res) => {
            if (ignore) return;
            setTsChart(res.status ? res.chart : {});
            setTsChartLoading(false);
        });
        return () => { ignore = true; };
    }, [tsYear]);

    // 5 thẻ chỉ số tổng quan Telesale
    const tsSummary = tsData?.summary;
    const telesaleMetricCards = [
        { id: 'booked', label: 'Lịch hẹn đã đặt', value: (tsSummary?.total_booked ?? 0).toLocaleString('vi-VN'), icon: CalendarDays, color: 'bg-blue-500' },
        { id: 'showup', label: 'Khách đến Showroom', value: (tsSummary?.total_showup ?? 0).toLocaleString('vi-VN'), icon: CalendarCheck, color: 'bg-cyan-500' },
        { id: 'showuprate', label: 'Tỷ lệ Show-up', value: `${(tsSummary?.showup_rate ?? 0).toFixed(2)}%`, icon: Clock, color: 'bg-orange-500' },
        { id: 'closed', label: 'Khách chốt dịch vụ', value: (tsSummary?.total_closed ?? 0).toLocaleString('vi-VN'), icon: CheckCircle2, color: 'bg-green-500' },
        { id: 'closerate', label: 'Tỷ lệ Chốt Cuối', value: `${(tsSummary?.close_rate ?? 0).toFixed(2)}%`, icon: Target, color: 'bg-purple-500' },
    ];

    // Kết quả cuộc gọi của khách hàng gốc (donut)
    const TS_CALL_COLORS = ['#10b981', '#94a3b8', '#f59e0b', '#f43f5e', '#3b82f6', '#8b5cf6', '#ef4444'];
    const tsCallDataAll = (tsData?.call_result_summary || []).map((c, i) => ({
        name: c.name,
        value: c.total,
        ratio: c.ratio,
        color: TS_CALL_COLORS[i % TS_CALL_COLORS.length],
    }));
    // Tab Zalo: API chưa trả kết quả tiếp cận qua Zalo -> giữ giao diện, hiện "Chưa có dữ liệu"
    const tsCallData = performanceTab === 'phone' ? tsCallDataAll : [];
    const tsCallTotal = tsCallData.reduce((sum, c) => sum + c.value, 0);

    // Biểu đồ trạng thái lịch hẹn 12 tháng
    const tsOrderStatusData = Array.from({ length: 12 }, (_, i) => {
        const m = tsChart[String(i + 1)];
        return {
            name: `T${i + 1}`,
            newOrders: m?.new ?? 0,
            successfulOrders: m?.success ?? 0,
            cancelledOrders: m?.canceled ?? 0,
        };
    });

    // Phễu chuyển đổi telesale theo thẻ chăm sóc (sắp giảm dần để ra đúng hình phễu)
    const tsFunnelStages = [...(tsData?.tag_summary || [])]
        .sort((a, b) => b.total - a.total)
        .map((t, i, arr) => {
            const first = arr[0]?.total || 0;
            const next = arr[i + 1]?.total;
            const colors = ['#e11d48', '#9333ea', '#3b82f6', '#10b981', '#f59e0b', '#dc2626'];
            const widthOf = (v?: number) => (first > 0 && v !== undefined ? Math.max(15, (v / first) * 100) : 15);
            return {
                label: t.tag,
                amount: Number(t.total).toLocaleString('vi-VN'),
                value: Number(t.total),
                color: colors[i % colors.length],
                width: widthOf(t.total),
                nextWidth: widthOf(next !== undefined ? next : t.total),
            };
        });

    // Top dịch vụ chốt lịch
    const tsServiceData = (tsData?.service_rankings || []).map((s) => ({
        name: s.name.length > 22 ? `${s.name.slice(0, 22)}…` : s.name,
        fullName: s.name,
        booked: s.booked,
        closed: s.closed,
        ratio: s.ratio,
    }));

    // Bảng xếp hạng nhân viên telesale
    const tsRankings = tsData?.rankings || [];

    // ===== Sale Cơ Sở (API /dashboard/sale-branch-performance) =====
    const [sbData, setSbData] = useState<SaleBranchPerformanceData | null>(null);
    const [sbLoading, setSbLoading] = useState(false);

    useEffect(() => {
        let ignore = false;
        setSbLoading(true);
        getSaleBranchPerformance(
            perfRange[0].format('YYYY-MM-DD'),
            perfRange[1].format('YYYY-MM-DD'),
            tsBranchId
        ).then((res) => {
            if (ignore) return;
            setSbData(res.status && res.data ? res.data : null);
            setSbLoading(false);
        });
        return () => { ignore = true; };
    }, [perfRange, tsBranchId]);

    const sbSummary = sbData?.summary;
    const sbRankings = sbData?.rankings || [];

    // Phễu trạng thái khách đến cơ sở
    const sbStatusData = (sbData?.status_summary || []).map((s) => ({
        stage: s.name,
        value: s.total,
        tag: s.tag,
    }));

    // Lưu lượng khách đến cơ sở theo giờ (traffic_statistics của API sale-branch-performance)
    const sbTraffic = sbData?.traffic_statistics;
    const sbTrafficData = (sbTraffic?.hours || []).map((h, i) => ({
        time: h,
        today: Number(sbTraffic?.today?.[i]) || 0,
        yesterday: Number(sbTraffic?.yesterday?.[i]) || 0,
    }));
    const sbTotalToday = sbTraffic?.summary?.total_today ?? 0;
    const sbTotalYesterday = sbTraffic?.summary?.total_yesterday ?? 0;
    const sbTrafficDiff = sbTraffic?.summary?.diff ?? 0;
    // API chỉ trả chênh lệch tuyệt đối, phần trăm tính ở FE
    const sbTrafficPercent = sbTotalYesterday > 0
        ? (sbTrafficDiff / sbTotalYesterday) * 100
        : (sbTotalToday > 0 ? 100 : 0);

    // ===== Biểu đồ 12 tháng các Cơ sở (API /dashboard/sale-branch-statistics) =====
    const [sbsYear, setSbsYear] = useState(currentYear);
    const [sbsData, setSbsData] = useState<SaleBranchStatisticsData | null>(null);
    const [sbsLoading, setSbsLoading] = useState(false);

    useEffect(() => {
        let ignore = false;
        setSbsLoading(true);
        getSaleBranchStatistics(sbsYear).then((res) => {
            if (ignore) return;
            setSbsData(res.status && res.data ? res.data : null);
            setSbsLoading(false);
        });
        return () => { ignore = true; };
    }, [sbsYear]);

    // Mỗi chi nhánh là một cột xếp chồng, khóa dữ liệu lấy từ success_branch_{id}
    const sbsBranches = sbsData?.branches || [];
    const sbsTrendData = (sbsData?.months || []).map((m, i) => {
        const row: any = {
            name: m,
            revenue: Number(((sbsData?.revenue_s8?.[i] || 0) / 1000000).toFixed(1)),
            aov: Number(((sbsData?.aov_s8?.[i] || 0) / 1000000).toFixed(1)),
            highValueOrders: Number(sbsData?.vip_orders_s8?.[i]) || 0,
        };
        sbsBranches.forEach((b) => {
            row[`branch_${b.id}`] = Number(sbsData?.[`success_branch_${b.id}`]?.[i]) || 0;
        });
        return row;
    });

    // Dịch vụ chốt/upsell nhiều nhất (sắp giảm dần theo doanh thu)
    const sbServiceData = [...(sbData?.service_summary || [])]
        .sort((a, b) => b.revenue - a.revenue)
        .map((s) => ({
            name: s.name.length > 24 ? `${s.name.slice(0, 24)}…` : s.name,
            fullName: s.name,
            code: s.code,
            quantity: s.total,
            revenue: s.revenue,
        }));

    // Hiệu suất trực page (API /dashboard/page-performance) — dùng chung perfRange ở trên
    const [perfData, setPerfData] = useState<PagePerformanceData | null>(null);
    const [perfLoading, setPerfLoading] = useState(false);
    // Lịch hẹn thành công (status 4 - Đã thực hiện) trong kỳ
    const [completedAppts, setCompletedAppts] = useState<number | null>(null);

    useEffect(() => {
        let ignore = false;
        setPerfLoading(true);
        const from = perfRange[0].format('YYYY-MM-DD');
        const to = perfRange[1].format('YYYY-MM-DD');
        Promise.all([
            getPagePerformance(from, to),
            getAppointments({ status: 4, date_from: from, date_to: to, limit: 1 }),
        ]).then(([res, apptRes]) => {
            if (ignore) return;
            setPerfData(res.status && res.data ? res.data : null);
            setCompletedAppts(apptRes?.status ? Number((apptRes.data as any)?.total ?? 0) : null);
            setPerfLoading(false);
        });
        return () => { ignore = true; };
    }, [perfRange]);

    // Phễu khách hàng theo thẻ S0-S4 (sắp giảm dần theo số khách để ra hình phễu)
    const tagFunnelData = [...(perfData?.customer_by_tags || [])]
        .sort((a, b) => b.count - a.count)
        .map((t, i, arr) => {
            const first = arr[0]?.count || 0;
            return {
                name: t.name.length > 30 ? `${t.name.slice(0, 30)}…` : t.name,
                value: t.count,
                rate: first > 0 ? Number(((t.count / first) * 100).toFixed(1)) : 0,
                color: t.color,
            };
        });

    // Kết quả cuộc gọi (màu theo id kết quả)
    const CALL_RESULT_COLORS: Record<string, string> = {
        'null': '#10b981', // Thành công
        '0': '#94a3b8',    // Chưa gọi
        '1': '#f59e0b',    // Không nghe máy
        '2': '#ec4899',    // Từ chối cuộc gọi
        '3': '#ef4444',    // Sai số điện thoại
        '4': '#64748b',    // Thuê bao không tồn tại
        '5': '#3b82f6',    // Không liên lạc được
    };
    const callResultData = (perfData?.call_result_summary || []).map((c) => ({
        name: c.name,
        value: c.count,
        ratio: c.ratio,
        color: CALL_RESULT_COLORS[String(c.id)] || '#8b5cf6',
    }));
    const callResultTotal = callResultData.reduce((sum, c) => sum + c.value, 0);

    // Nguồn khách theo kênh (fanpage), sắp xếp giảm dần
    const channelData = [...(perfData?.customer_by_channels || [])]
        .sort((a, b) => b.count - a.count)
        .map((c) => ({ name: c.name, value: c.count }));

    // So sánh hiệu suất nhân viên trực page (Top 10 theo số khách chat)
    const staffChartData = [...(perfData?.staff_customer_stats || [])]
        .sort((a, b) => b.channel_customer_count - a.channel_customer_count)
        .slice(0, 10)
        .map((s) => ({
            name: s.staff_name,
            chats: s.channel_customer_count,
            leads: s.customer_count,
        }));

    // Doanh thu theo nền tảng
    const platformData = (perfData?.revenue_by_platforms || []).map((p) => {
        const key = p.name.toLowerCase();
        const color = key.includes('facebook') ? '#3b82f6' : key.includes('zalo') ? '#06b6d4' : key.includes('tiktok') ? '#0f172a' : '#8b5cf6';
        return { name: p.name, revenue: p.revenue, color };
    });

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') direction = 'asc';
        setSortConfig({ key, direction });
    };

    const getSortedEmployees = () => {
        const calculateRate = (emp, key) => {
            if (key === 'contactRate') return (emp.reached / emp.called) * 100 || 0;
            if (key === 'bookingRate') return (emp.booked / emp.reached) * 100 || 0;
            if (key === 'showRate') return (emp.showed / emp.booked) * 100 || 0;
            if (key === 'closeRate') return (emp.closed / emp.showed) * 100 || 0;
            return emp[key];
        };

        return [...tsEmployeeData].sort((a, b) => {
            const aVal = calculateRate(a, sortConfig.key);
            const bVal = calculateRate(b, sortConfig.key);
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const tabs = [
        // { id: 'overview', label: 'Góc nhìn Quản trị' },
        { id: 'page', label: 'Trực Page' },
        { id: 'telesale', label: 'Telesale' },
        { id: 'onsite', label: 'Sale Cơ Sở' },
        // { id: 'tech', label: 'Kỹ Thuật' },
        // { id: 'topsales', label: 'Top Kinh Doanh' },
    ];

    const renderOverview = () => (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Tổng Doanh Thu (TrVND)" value="2,120" subValue="+15% so với tháng trước" icon={DollarSign} colorClass="bg-blue-500" trend="up" />
                <StatCard title="Tổng Data Tạo Mới" value="5,800" subValue="+8% so với tháng trước" icon={Users} colorClass="bg-emerald-500" trend="up" />
                <StatCard title="Tỉ lệ chốt (Tổng thể)" value="13.4%" subValue="Mục tiêu: 15%" icon={Target} colorClass="bg-amber-500" />
                <StatCard title="Chi phí / Khách hàng (CAC)" value="450K" subValue="-5% so với tháng trước" icon={Activity} colorClass="bg-purple-500" trend="down" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Thống kê Chiến dịch & Doanh thu</h3>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={overviewData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" />
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip />
                                <Legend />
                                <Bar yAxisId="left" dataKey="revenue" name="Doanh thu thực (Tr)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                <Line yAxisId="left" type="monotone" dataKey="target" name="Mục tiêu (Tr)" stroke="#ef4444" strokeWidth={2} />
                                <Area yAxisId="right" type="monotone" dataKey="leads" name="Lượng Data mới" fill="#10b981" stroke="#10b981" fillOpacity={0.1} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Phễu Chuyển Đổi Hệ Thống (Conversion Funnel)</h3>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={systemFunnel} layout="vertical" margin={{ left: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" />
                                <YAxis dataKey="stage" type="category" width={120} />
                                <Tooltip />
                                <Bar dataKey="value" name="Số lượng" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                                    {systemFunnel.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPage = () => (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                <p className="font-semibold text-slate-700">Khoảng thời gian thống kê</p>
                <DatePicker.RangePicker
                    value={perfRange}
                    onChange={(dates) => { if (dates && dates[0] && dates[1]) setPerfRange([dates[0], dates[1]]); }}
                    allowClear={false}
                    format="DD/MM/YYYY"
                />
            </div> */}

            <Spin spinning={perfLoading}>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    <StatCard title="Tổng tin nhắn mới" value={(perfData?.summary.total_chat_customers ?? 0).toLocaleString('vi-VN')} icon={MessageCircle} colorClass="bg-gradient-to-br from-blue-400 to-blue-600" />
                    <StatCard title="Khách hàng tạo mới" value={(perfData?.summary.total_new_customers ?? 0).toLocaleString('vi-VN')} icon={Users} colorClass="bg-gradient-to-br from-indigo-400 to-indigo-600" />
                    <StatCard title="Tổng số lịch hẹn" value={(perfData?.summary.total_appointments ?? 0).toLocaleString('vi-VN')} icon={Target} colorClass="bg-gradient-to-br from-emerald-400 to-emerald-600" />
                    <StatCard title="Lịch hẹn thành công" value={completedAppts === null ? 'Đang chờ' : completedAppts.toLocaleString('vi-VN')} icon={CalendarCheck} colorClass="bg-gradient-to-br from-emerald-400 to-emerald-600" />
                </div>
            </Spin>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                    <h3 className="text-lg font-bold text-slate-800">
                        Biến động Doanh thu, Khách hàng mới & Lịch hẹn hoàn thành (Năm {statsYear})
                    </h3>
                    <Select
                        value={statsYear}
                        onChange={(value) => setStatsYear(value)}
                        options={yearOptions}
                        className="w-[140px]"
                        popupMatchSelectWidth={false}
                    />
                </div>
                <Spin spinning={statsLoading}>
                    <div className="h-[320px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={statsChartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontWeight: 500 }} dy={10} />
                                <YAxis yAxisId="left" tickLine={false} axisLine={false} tickFormatter={(value) => `${(value / 1000000).toLocaleString('vi-VN')}Tr`} tick={{ fill: '#64748b', fontWeight: 500 }} dx={-10} />
                                <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} tickFormatter={(value) => Number(value).toLocaleString('vi-VN')} tick={{ fill: '#64748b', fontWeight: 500 }} dx={10} />
                                <Tooltip
                                    contentStyle={chartTooltipStyle}
                                    cursor={{ fill: '#f1f5f9' }}
                                    formatter={(value, name) =>
                                        name === 'Doanh thu'
                                            ? [`${Number(value).toLocaleString('vi-VN')} đ`, name]
                                            : [Number(value).toLocaleString('vi-VN'), name]
                                    }
                                />
                                <Legend wrapperStyle={{ paddingTop: '20px', fontWeight: 500 }} />
                                <Area yAxisId="left" type="monotone" dataKey="revenue" name="Doanh thu" fill="url(#colorRev)" stroke="#3b82f6" strokeWidth={3} />
                                <Line yAxisId="right" type="monotone" dataKey="customers" name="Khách hàng mới" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                <Line yAxisId="right" type="monotone" dataKey="appointments" name="Lịch hẹn hoàn thành" stroke="#10b981" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                <defs>
                                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </Spin>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Phễu chuyển đổi khách hàng (Sales Funnel)</h3>
                    <Spin spinning={perfLoading}>
                        <div className="h-[300px] flex items-center justify-center">
                            {tagFunnelData.length > 0 ? (
                                <CustomFunnelChart data={tagFunnelData} />
                            ) : (
                                <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                            )}
                        </div>
                    </Spin>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                        <PhoneCall className="w-5 h-5 text-blue-600" /> Thống kê Kết quả cuộc gọi
                    </h3>
                    <Spin spinning={perfLoading}>
                        {callResultTotal > 0 ? (
                            <div className="flex flex-col lg:flex-row items-center gap-4">
                                {/* Donut chỉ vẽ được phần > 0 */}
                                <div className="w-full lg:w-[45%] h-[220px] relative shrink-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={callResultData.filter((c) => c.value > 0)} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                                                paddingAngle={4} dataKey="value" stroke="none"
                                            >
                                                {callResultData.filter((c) => c.value > 0).map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} className="hover:opacity-80 transition-opacity cursor-pointer" />)}
                                            </Pie>
                                            <Tooltip contentStyle={chartTooltipStyle} formatter={(value) => `${Number(value).toLocaleString('vi-VN')} khách`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex items-center justify-center flex-col pointer-events-none">
                                        <span className="text-2xl font-bold text-slate-800">{callResultTotal.toLocaleString('vi-VN')}</span>
                                        <span className="text-xs text-slate-500">Tổng khách</span>
                                    </div>
                                </div>

                                {/* Chú thích đầy đủ — giữ cả trạng thái có giá trị 0 để không gây hiểu nhầm */}
                                <div className="w-full lg:flex-1 space-y-1.5">
                                    {callResultData.map((item, index) => {
                                        const isZero = item.value === 0;
                                        return (
                                            <div
                                                key={index}
                                                className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border ${isZero ? 'border-slate-100 bg-slate-50/60' : 'border-slate-100 hover:bg-slate-50'} transition-colors`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span
                                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                                        style={{ background: isZero ? '#e2e8f0' : item.color }}
                                                    />
                                                    <span className={`text-[13px] font-medium truncate ${isZero ? 'text-slate-400' : 'text-slate-700'}`} title={item.name}>
                                                        {item.name}
                                                    </span>
                                                </div>
                                                <div className="flex items-baseline gap-1.5 shrink-0">
                                                    <span className={`text-sm font-bold ${isZero ? 'text-slate-400' : 'text-slate-800'}`}>
                                                        {item.value.toLocaleString('vi-VN')}
                                                    </span>
                                                    <span className={`text-[11px] font-semibold px-1.5 rounded ${isZero ? 'text-slate-400 bg-slate-100' : 'text-slate-500 bg-slate-100'}`}>
                                                        {Number(item.ratio ?? 0).toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center">
                                <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                            </div>
                        )}
                    </Spin>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Nguồn Data từ các Fanpage</h3>
                    <Spin spinning={perfLoading}>
                        <div className="h-[300px]">
                            {channelData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={channelData} layout="vertical" margin={{ left: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                        <XAxis type="number" tickLine={false} axisLine={false} />
                                        <YAxis dataKey="name" type="category" width={120} tick={<ChannelAxisTick />} tickLine={false} axisLine={false} />
                                        <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: '#f1f5f9' }} formatter={(value) => [`${Number(value).toLocaleString('vi-VN')}`, 'Số khách']} />
                                        <Bar dataKey="value" name="Số khách" fill="#8b5cf6" radius={[0, 6, 6, 0]} barSize={24}>
                                            {channelData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                                </div>
                            )}
                        </div>
                    </Spin>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Doanh thu theo Nền tảng</h3>
                    <Spin spinning={perfLoading}>
                        <div className="h-[300px]">
                            {platformData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={platformData} barSize={40}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontWeight: 500, fill: '#64748b', fontSize: 12 }} dy={10} />
                                        <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => `${(value / 1000000).toLocaleString('vi-VN')}Tr`} tick={{ fontWeight: 500, fill: '#64748b' }} dx={-10} />
                                        <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: '#f1f5f9' }} formatter={(value) => [`${Number(value).toLocaleString('vi-VN')} đ`, 'Doanh thu']} />
                                        <Bar dataKey="revenue" name="Doanh thu" radius={[4, 4, 0, 0]}>
                                            {platformData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                                </div>
                            )}
                        </div>
                    </Spin>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow mt-6">
                <h3 className="text-lg font-bold text-slate-800 mb-6">So Sánh Hiệu Suất Nhân Viên Trực Page (Top 10)</h3>
                <Spin spinning={perfLoading}>
                    <div className="h-[350px]">
                        {staffChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={staffChartData}
                                    margin={{
                                        top: 30,
                                        right: -15,
                                        left: -15,
                                        bottom: 0,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 500, fill: '#64748b' }} interval={0} angle={-15} textAnchor="end" height={60} tickLine={false} axisLine={false} dy={10} />
                                    <YAxis yAxisId="left" tickLine={false} axisLine={false} tick={{ fontWeight: 500, fill: '#64748b' }} dx={-10} />
                                    <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} tick={{ fontWeight: 500, fill: '#64748b' }} dx={10} />
                                    <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: '#f1f5f9' }} />
                                    <Legend wrapperStyle={{ top: -10, fontWeight: 500 }} />
                                    <Bar yAxisId="left" dataKey="chats" name="Tổng Chat Xử Lý" fill="#cbd5e1" radius={[4, 4, 0, 0]} maxBarSize={40} className="hover:opacity-80 transition-opacity" />
                                    <Line yAxisId="right" type="monotone" dataKey="leads" name="Số Lead Tạo Ra" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6, fill: '#8b5cf6' }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                            </div>
                        )}
                    </div>
                </Spin>
            </div>
        </div>
    );

    const renderTelesale = () => {
        const sortedEmployees = getSortedEmployees();
        return (
            <div className="space-y-6 animate-in fade-in duration-500">
                {/* Bộ lọc thời gian + chi nhánh đã gộp lên thanh trên cùng của trang */}

                <Spin spinning={tsLoading}>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {telesaleMetricCards.map((card) => (
                        <div key={card.id} className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex flex-col relative overflow-hidden group hover:shadow-md transition-shadow">
                            <div className={`absolute top-0 right-0 w-16 h-16 opacity-10 rounded-bl-full ${card.color} -mr-4 -mt-4 transition-transform group-hover:scale-110`}></div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${card.color} shadow-sm`}>
                                    <card.icon size={20} />
                                </div>
                                <span className="text-sm font-medium text-gray-500">{card.label}</span>
                            </div>
                            <div className="flex items-end justify-between mt-auto">
                                <h3 className="text-2xl font-bold text-gray-800">{card.value}</h3>
                            </div>
                        </div>
                    ))}
                </div>
                </Spin>

                {/* Hiệu suất cuộc gọi (Donut) - ĐÃ ĐƯỢC CHUYỂN LÊN TRƯỚC */}
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            {performanceTab === 'phone' ? (
                                <PhoneCall className="text-blue-500" size={20} />
                            ) : (
                                <MessageSquare className="text-blue-500" size={20} />
                            )}
                            Kết quả tiếp cận khách hàng
                        </h2>

                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setPerformanceTab('phone')}
                                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${performanceTab === 'phone'
                                    ? 'bg-white shadow-sm text-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Điện thoại
                            </button>
                            <button
                                onClick={() => setPerformanceTab('zalo')}
                                className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-all ${performanceTab === 'zalo'
                                    ? 'bg-white shadow-sm text-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Zalo
                            </button>
                        </div>
                    </div>

                    <Spin spinning={tsLoading}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Chart */}
                        <div className="col-span-1 h-[220px] relative">
                            {tsCallTotal > 0 ? (
                                <>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={tsCallData.filter((c) => c.value > 0)}
                                                innerRadius={60}
                                                outerRadius={90}
                                                paddingAngle={2}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {tsCallData.filter((c) => c.value > 0).map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip formatter={(value) => [`${Number(value).toLocaleString('vi-VN')} khách`, '']} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex items-center justify-center flex-col pointer-events-none">
                                        <span className="text-3xl font-bold text-gray-800">{tsCallTotal.toLocaleString('vi-VN')}</span>
                                        <span className="text-xs text-gray-500">Tổng khách</span>
                                    </div>
                                </>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                                </div>
                            )}
                        </div>
                        {/* Legend Grid */}
                        <div className="col-span-1 lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
                            {tsCallData.map((item, index) => (
                                <div key={index} className="flex flex-col p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                        <span className="text-sm font-semibold text-gray-700 line-clamp-1" title={item.name}>{item.name}</span>
                                    </div>
                                    <div className="flex items-end gap-2">
                                        <span className="text-xl font-bold text-gray-900">{item.value.toLocaleString('vi-VN')} <span className="text-xs font-normal text-gray-500">khách</span></span>
                                        <span className="text-xs text-gray-500 font-medium bg-white border border-gray-200 px-1.5 rounded ml-auto shadow-sm">
                                            {Number(item.ratio).toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    </Spin>
                </div>

                {/* Biểu đồ Doanh thu 12 Tháng & So sánh - ĐÃ BỊ CHUYỂN XUỐNG */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    <div className="lg:col-span-3 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <BarChart2 className="text-blue-500" size={20} /> Biểu đồ Trạng thái Lịch hẹn (Năm {tsYear})
                            </h2>
                            <Select
                                value={tsYear}
                                onChange={(v) => setTsYear(v)}
                                options={yearOptions}
                                className="w-[140px]"
                                popupMatchSelectWidth={false}
                            />
                        </div>
                        <Spin spinning={tsChartLoading}>
                        <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={tsOrderStatusData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                    <RechartsTooltip cursor={{ fill: '#f3f4f6' }} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                    <Bar dataKey="newOrders" name="Lịch hẹn mới" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={20} />
                                    <Bar dataKey="successfulOrders" name="Lịch hẹn thành công" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={20} />
                                    <Line type="monotone" dataKey="cancelledOrders" name="Lịch hẹn hủy" stroke="#ef4444" strokeWidth={2} dot={{ r: 4, fill: '#ef4444', strokeWidth: 2, stroke: '#fff' }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                        </Spin>
                    </div>

                    <div className="lg:col-span-1 bg-gradient-to-br bg-[#2563eb] from-blue-600 to-indigo-700 p-6 rounded-2xl shadow-md flex flex-col justify-center relative overflow-hidden group">
                        <div className="absolute top-4 right-4 opacity-10 group-hover:scale-125 transition-transform duration-700">
                            <DollarSign className="w-32 h-32 text-white" />
                        </div>
                        <h4 className="text-sm font-bold text-blue-200 uppercase tracking-wider mb-6 relative z-10 flex items-center gap-2">
                            <Activity className="w-4 h-4" /> So sánh doanh thu
                        </h4>
                        <div className="space-y-6 relative z-10">
                            <div>
                                <p className="text-xs font-medium text-blue-200 mb-1">Kỳ hiện tại</p>
                                <div className="flex items-baseline gap-1">
                                    <p className="text-4xl font-black text-white">
                                        {((tsData?.revenue?.current_period ?? 0) / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}
                                    </p>
                                    <span className="text-lg font-bold text-blue-300">Tr</span>
                                </div>
                            </div>
                            <div className="h-px w-full bg-blue-400/30"></div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium text-blue-200 mb-1">Kỳ trước</p>
                                    <p className="text-xl font-bold text-white">
                                        {((tsData?.revenue?.prev_period ?? 0) / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} Tr
                                    </p>
                                </div>
                                {(() => {
                                    const pct = tsData?.revenue?.difference_percent ?? 0;
                                    const up = (tsData?.revenue?.difference ?? 0) >= 0;
                                    return (
                                        <div className={`px-3 py-1.5 rounded-lg flex items-center gap-1 font-black shadow-lg ${up ? 'bg-emerald-400 text-emerald-950' : 'bg-rose-400 text-rose-950'}`}>
                                            {up ? <TrendingUp className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                                            {up ? '+' : ''}{Number(pct).toFixed(1)}%
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Phễu & Dịch vụ */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center hover:shadow-md transition-shadow">
                        <h3 className="text-lg font-bold text-slate-800 self-start mb-2 flex items-center gap-2">
                            <Target className="w-5 h-5 text-indigo-500" /> Phễu Chuyển Đổi Telesale (SVG)
                        </h3>
                        <Spin spinning={tsLoading} wrapperClassName="w-full">
                            {tsFunnelStages.length > 0 ? (
                                <CustomSVGFunnel data={tsFunnelStages} />
                            ) : (
                                <div className="h-[300px] flex items-center justify-center">
                                    <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                                </div>
                            )}
                        </Spin>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-emerald-500" /> Top Dịch vụ chốt lịch
                        </h3>
                        <Spin spinning={tsLoading}>
                        <div className="h-80">
                            {tsServiceData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={tsServiceData} margin={{ top: 20, right: 20, bottom: 0, left: -20 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={60} />
                                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            cursor={{ fill: '#f1f5f9' }}
                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            labelFormatter={(_, payload: any) => payload?.[0]?.payload?.fullName || ''}
                                        />
                                        <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                                        <Bar dataKey="booked" name="Lịch hẹn đã đặt" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                        <Line type="monotone" dataKey="closed" name="Đã chốt dịch vụ" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                                </div>
                            )}
                        </div>
                        </Spin>
                    </div>
                </div>

                {/* Biểu đồ Chất lượng 10 Nhân Viên */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-purple-500" /> Phân tích Hiệu suất Nhân viên Telesale
                    </h3>
                    <Spin spinning={tsLoading}>
                    <div className="h-96 w-full">
                        {tsRankings.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={tsRankings} margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis dataKey="name" angle={-15} textAnchor="end" height={60} tick={{ fontSize: 12, fill: '#475569' }} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                    <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '13px', fontWeight: 600 }} />

                                    <Bar yAxisId="left" dataKey="booked" name="Lịch hẹn đã đặt" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={20} />
                                    <Bar yAxisId="left" dataKey="showup" name="Khách đến Showroom" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={20} />
                                    <Line yAxisId="right" type="monotone" dataKey="closed" name="Đã Chốt Sale (Win)" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                            </div>
                        )}
                    </div>
                    </Spin>
                </div>

                {/* Bảng Leaderboard (Mở rộng toàn bộ) */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow overflow-x-auto">
                    <div className="flex justify-between items-center mb-6 min-w-[700px]">
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-500" /> Bảng Xếp Hạng Nhân Viên
                        </h3>
                        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-md">Click tiêu đề cột để sắp xếp</span>
                    </div>
                    <Spin spinning={tsLoading}>
                    <table className="w-full text-left whitespace-nowrap min-w-[900px]">
                        <thead>
                            <tr className="bg-slate-50 text-slate-600 text-xs uppercase font-bold border-b border-slate-200">
                                <th className="p-4 rounded-tl-lg">Nhân viên</th>
                                <th className="p-4 text-center">Hạng</th>
                                <th className="p-4">Lịch hẹn đã đặt</th>
                                <th className="p-4">Show Rate</th>
                                <th className="p-4">Close Rate</th>
                                <th className="p-4 text-right rounded-tr-lg">Doanh thu</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {tsRankings.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="p-10 text-center text-slate-400 font-medium">Chưa có dữ liệu</td>
                                </tr>
                            )}
                            {tsRankings.map((emp) => {
                                const showRate = Number(emp.showup_rate || 0).toFixed(1);
                                const closeRate = Number(emp.close_rate || 0).toFixed(1);
                                return (
                                    <tr key={emp.staff_id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="p-4 font-bold text-slate-800 flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs group-hover:bg-blue-600 group-hover:text-white transition-colors overflow-hidden">
                                                {emp.avatar
                                                    ? <img src={emp.avatar} alt={emp.name} className="w-full h-full object-cover" />
                                                    : emp.name.charAt(0).toUpperCase()}
                                            </div>
                                            {emp.name}
                                        </td>
                                        <td className="p-4 text-center font-mono text-sm bg-slate-50/50">
                                            <span className="font-semibold text-slate-800">#{emp.rank}</span>
                                        </td>
                                        <td className="p-4 w-40 border-r border-slate-50">
                                            <span className="font-bold text-slate-700">{Number(emp.booked || 0).toLocaleString('vi-VN')}</span>
                                        </td>
                                        <td className="p-4 w-40 border-r border-slate-50">
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="font-bold text-slate-700">{emp.showup}</span>
                                                <span className="text-purple-600 font-bold">{showRate}%</span>
                                            </div>
                                            <div className="w-full bg-purple-100 rounded-full h-1.5"><div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, Number(showRate))}%` }}></div></div>
                                        </td>
                                        <td className="p-4 w-40 border-r border-slate-50">
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="font-bold text-slate-700">{emp.closed}</span>
                                                <span className="text-emerald-600 font-bold">{closeRate}%</span>
                                            </div>
                                            <div className="w-full bg-emerald-100 rounded-full h-1.5"><div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, Number(closeRate))}%` }}></div></div>
                                        </td>
                                        {/* Doanh thu theo từng nhân viên: API chưa trả -> giữ cột, hiện "—" */}
                                        <td className="p-4 font-bold text-slate-400 text-right bg-slate-50">—</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </Spin>
                </div>
            </div>
        );
    };

    const renderOnsite = () => (
        <div className="space-y-6 animate-in fade-in duration-500">
            <Spin spinning={sbLoading}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                        title="Tổng Khách đến cơ sở"
                        value={(sbSummary?.total_showup ?? 0).toLocaleString('vi-VN')}
                        subValue="Khách show-up trong kỳ"
                        icon={Users}
                        colorClass="bg-blue-500"
                    />
                    <StatCard
                        title="Tỉ lệ chốt (Sale Cơ sở)"
                        value={`${(sbSummary?.close_rate ?? 0).toFixed(2)}%`}
                        subValue={`${(sbSummary?.total_closed ?? 0).toLocaleString('vi-VN')} khách chốt dịch vụ`}
                        icon={Target}
                        colorClass="bg-emerald-500"
                    />
                    <StatCard
                        title="Đơn trung bình (AOV)"
                        value={`${((sbSummary?.avg_order_value ?? 0) / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} Tr`}
                        subValue="Doanh thu / khách chốt"
                        icon={DollarSign}
                        colorClass="bg-purple-500"
                    />
                    <StatCard
                        title="Tổng doanh thu"
                        value={`${((sbSummary?.total_revenue ?? 0) / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} Tr`}
                        subValue="Doanh thu chốt tại cơ sở"
                        icon={Award}
                        colorClass="bg-amber-500"
                    />
                </div>
            </Spin>

            { }
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-500" /> Lưu lượng khách đến cơ sở theo giờ (Hôm nay vs Hôm qua)
                    </h3>
                    <Spin spinning={sbLoading} wrapperClassName="w-full">
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={sbTrafficData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                                <defs>
                                    <linearGradient id="colorToday" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="time" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                                <Area type="monotone" dataKey="yesterday" name="Hôm qua" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" fill="none" />
                                <Area type="monotone" dataKey="today" name="Hôm nay" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorToday)" activeDot={{ r: 6 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    </Spin>
                </div>

                <div className="lg:col-span-1 bg-gradient-to-br from-indigo-600 bg-[#2563eb] to-purple-700 p-6 rounded-2xl shadow-md flex flex-col justify-center relative overflow-hidden group">
                    <div className="absolute top-4 right-4 opacity-10 group-hover:scale-125 transition-transform duration-700">
                        <Users className="w-32 h-32 text-white" />
                    </div>
                    <h4 className="text-sm font-bold text-indigo-200 uppercase tracking-wider mb-6 relative z-10 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Tổng khách Hôm nay
                    </h4>
                    <div className="space-y-6 relative z-10">
                        <div>
                            <div className="flex items-baseline gap-2">
                                <p className="text-5xl font-black text-white">{sbTotalToday.toLocaleString('vi-VN')}</p>
                                <span className="text-lg font-bold text-indigo-200">khách</span>
                            </div>
                        </div>
                        <div className="h-px w-full bg-indigo-400/30"></div>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-medium text-indigo-200 mb-1">
                                    So với hôm qua ({sbTotalYesterday.toLocaleString('vi-VN')} khách)
                                </p>
                            </div>
                            <div
                                className={`px-3 py-1.5 rounded-lg flex items-center gap-1 font-black shadow-lg text-sm ${sbTrafficDiff < 0 ? 'bg-rose-400 text-rose-950' : 'bg-emerald-400 text-emerald-950'
                                    }`}
                            >
                                {sbTrafficDiff < 0 ? <ArrowDownRight className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                                {`${sbTrafficPercent >= 0 ? '+' : ''}${sbTrafficPercent.toFixed(1)}%`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Biểu đồ Phân tích Doanh số & Khách hàng */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-indigo-500" /> Phân tích Doanh số, Lượng khách & Đơn hàng các Cơ sở (Năm {sbsYear})
                    </h3>
                    <Select
                        value={sbsYear}
                        onChange={(value) => setSbsYear(value)}
                        options={yearOptions}
                        className="w-[140px]"
                        popupMatchSelectWidth={false}
                    />
                </div>
                <Spin spinning={sbsLoading} wrapperClassName="w-full">
                <div className="h-96 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={sbsTrendData} margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />

                            {/* Trục trái: Doanh thu */}
                            <YAxis yAxisId="left" tickFormatter={(v) => `${v}Tr`} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />

                            {/* Trục phải: Số khách & Số đơn */}
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />

                            {/* Trục ẩn thứ 3: Dành riêng cho Đơn TB (AOV) để scale đẹp */}
                            {/* Neo mốc 0 ở đáy để tháng không có dữ liệu đường AOV nằm sát trục, không bị đẩy lên giữa biểu đồ */}
                            <YAxis yAxisId="aovAxis" orientation="right" hide={true} domain={[0, 'dataMax + 0.5']} />

                            <Tooltip content={<CustomOnsiteTooltip />} cursor={{ fill: '#f1f5f9' }} />
                            <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />

                            {/* Các cột Khách hàng xếp chồng — mỗi chi nhánh một màu, lấy theo danh sách branches của API */}
                            {sbsBranches.map((b, i) => (
                                <Bar
                                    key={b.id}
                                    yAxisId="right"
                                    dataKey={`branch_${b.id}`}
                                    name={`Khách ${b.name}`}
                                    fill={COLORS[i % COLORS.length]}
                                    stackId="a"
                                    radius={i === sbsBranches.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                    maxBarSize={45}
                                />
                            ))}

                            {/* Các đường xu hướng */}
                            <Line yAxisId="right" type="monotone" dataKey="highValueOrders" name="Đơn giá trị cao (>10Tr)" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4, fill: '#fff', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                            <Line yAxisId="aovAxis" type="monotone" dataKey="aov" name="Đơn trung bình (AOV)" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#ef4444' }} activeDot={{ r: 5 }} />
                            <Line yAxisId="left" type="monotone" dataKey="revenue" name="Tổng Doanh Thu (Tr)" stroke="#f59e0b" strokeWidth={4} dot={{ r: 5, fill: '#f59e0b', strokeWidth: 2 }} activeDot={{ r: 8 }} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
                </Spin>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Phễu Trạng thái Khách đến Cơ sở</h3>
                    <Spin spinning={sbLoading}>
                        <div className="h-80">
                            {sbStatusData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={sbStatusData} layout="vertical" margin={{ left: 40, right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                                        <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <YAxis dataKey="stage" type="category" width={130} tick={<ChannelAxisTick />} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            cursor={{ fill: '#f8fafc' }}
                                            contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            formatter={(value: any, _name: any, item: any) => [`${Number(value).toLocaleString('vi-VN')} khách`, item?.payload?.tag || 'Số lượng khách']}
                                        />
                                        <Bar dataKey="value" name="Số lượng khách" radius={[0, 4, 4, 0]} barSize={45}>
                                            {sbStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                                </div>
                            )}
                        </div>
                    </Spin>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-500" /> Top Dịch vụ thu hút Khách nhất
                    </h3>
                    <Spin spinning={sbLoading}>
                        <div className="space-y-4">
                            {sbServiceData.length === 0 && !sbLoading && (
                                <div className="py-10 text-center text-slate-400 font-medium">Chưa có dữ liệu</div>
                            )}
                            {sbServiceData.map((item, idx) => (
                                <div key={item.code || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-slate-100 transition-colors">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${idx === 0 ? 'bg-amber-100 text-amber-600' : idx === 1 ? 'bg-slate-200 text-slate-600' : idx === 2 ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-600'}`}>
                                            {idx + 1}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-semibold text-slate-800 text-sm truncate" title={item.fullName}>{item.fullName}</p>
                                            <p className="text-xs text-slate-500 font-medium">
                                                Doanh thu: {(item.revenue / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} Tr
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0 pl-3">
                                        <p className="font-bold text-emerald-600">{item.quantity}</p>
                                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Khách</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Spin>
                </div>
            </div>

            {/* Biểu đồ phân tích doanh thu Top dịch vụ */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" /> Top Dịch vụ Bán chạy nhất (Theo Doanh thu)
                </h3>
                <Spin spinning={sbLoading}>
                    <div className="h-80 w-full">
                        {sbServiceData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={sbServiceData} margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={60} />
                                    <YAxis yAxisId="left" tickFormatter={(v) => `${(v / 1000000).toLocaleString('vi-VN')}Tr`} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip
                                        cursor={{ fill: '#f1f5f9' }}
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        labelFormatter={(_, payload: any) => payload?.[0]?.payload?.fullName || ''}
                                        formatter={(value: any, name: any) =>
                                            String(name).includes('Doanh thu')
                                                ? [`${Number(value).toLocaleString('vi-VN')} đ`, name]
                                                : [`${Number(value).toLocaleString('vi-VN')}`, name]
                                        }
                                    />
                                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />

                                    <Bar yAxisId="left" dataKey="revenue" name="Doanh thu" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={45} />
                                    <Line yAxisId="right" type="monotone" dataKey="quantity" name="Số lượng khách (Ca)" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4, fill: '#fff', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center">
                                <p className="text-slate-400 font-medium">Chưa có dữ liệu</p>
                            </div>
                        )}
                    </div>
                </Spin>
            </div>

            {/* Bảng Xếp Hạng Nhân Viên Sale Cơ Sở */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow overflow-x-auto">
                <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" /> Bảng Xếp Hạng Nhân Viên (Dựa trên Doanh Thu)
                </h3>
                <Spin spinning={sbLoading}>
                    <table className="w-full text-left whitespace-nowrap min-w-[700px]">
                        <thead>
                            <tr className="bg-slate-50 text-slate-600 text-xs uppercase font-bold border-b border-slate-200">
                                <th className="p-4 rounded-tl-lg w-20">Hạng</th>
                                <th className="p-4">Nhân viên</th>
                                <th className="p-4 text-center">Khách Tư Vấn</th>
                                <th className="p-4 text-center">Khách Chốt</th>
                                <th className="p-4 text-center">Tỷ lệ Chốt</th>
                                <th className="p-4 text-right">Đơn TB (AOV)</th>
                                <th className="p-4 text-right rounded-tr-lg">Doanh Thu</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sbRankings.length === 0 && !sbLoading && (
                                <tr>
                                    <td colSpan={7} className="p-10 text-center text-slate-400 font-medium">Chưa có dữ liệu</td>
                                </tr>
                            )}
                            {sbRankings.map((emp, idx) => (
                                <tr key={emp.staff_id} className="hover:bg-slate-50 transition-colors">
                                    <td className="p-4 font-bold text-slate-800">
                                        {idx === 0 ? <span className="text-amber-500 text-xl">🥇 1</span> :
                                            idx === 1 ? <span className="text-slate-400 text-xl">🥈 2</span> :
                                                idx === 2 ? <span className="text-amber-700 text-xl">🥉 3</span> : <span className="pl-3 text-slate-500">#{emp.rank ?? idx + 1}</span>}
                                    </td>
                                    <td className="p-4 font-bold text-slate-800 flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs overflow-hidden">
                                            {emp.avatar
                                                ? <img src={emp.avatar} alt={emp.name} className="w-full h-full object-cover" />
                                                : emp.name.charAt(0).toUpperCase()}
                                        </div>
                                        {emp.name}
                                    </td>
                                    <td className="p-4 text-center font-medium text-slate-600">{Number(emp.showup || 0).toLocaleString('vi-VN')}</td>
                                    <td className="p-4 text-center font-bold text-emerald-600">{Number(emp.closed || 0).toLocaleString('vi-VN')}</td>
                                    <td className="p-4 text-center">
                                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold">{Number(emp.rate || 0).toFixed(1)}%</span>
                                    </td>
                                    <td className="p-4 font-medium text-slate-600 text-right">
                                        {((emp.aov || 0) / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} Tr
                                    </td>
                                    <td className="p-4 font-bold text-blue-700 text-right bg-blue-50/30">
                                        {((emp.revenue || 0) / 1000000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} Tr
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Spin>
            </div>
        </div>
    );

    const renderTech = () => (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Khách đang thực hiện" value="125" subValue="Có mặt tại phòng dịch vụ" icon={Activity} colorClass="bg-blue-500" />
                <StatCard title="Khách đang trong Liệu trình" value="850" subValue="Khách quay lại theo gói" icon={CalendarCheck} colorClass="bg-purple-500" />
                <StatCard title="Khách đã hoàn thành" value="655" subValue="Trong tháng hiện tại" icon={Award} colorClass="bg-emerald-500" />
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Tải công việc của Kỹ thuật viên (Hôm nay)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} className="p-4 border border-slate-100 rounded-xl bg-slate-50 flex flex-col items-center justify-center hover:bg-white hover:shadow-sm transition-all">
                            <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mb-2">
                                <User className="w-6 h-6 text-slate-500" />
                            </div>
                            <span className="font-bold text-slate-700">KTV {i}</span>
                            <span className={`text-xs font-semibold mt-1 px-3 py-1 rounded-full ${i % 3 === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                {i % 3 === 0 ? 'Đang trống' : 'Đang phục vụ'}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderTopSales = () => (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex items-center gap-3 mb-6">
                    <Trophy className="w-8 h-8 text-amber-500" />
                    <h3 className="text-xl font-bold text-slate-800">Vinh danh Top Nhân Viên Kinh Doanh (Tháng)</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topSalesData} layout="vertical" margin={{ left: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" />
                                <Tooltip />
                                <Bar dataKey="revenue" name="Doanh thu (Triệu VNĐ)" fill="#f59e0b" radius={[0, 4, 4, 0]}>
                                    {topSalesData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={index === 0 ? '#f59e0b' : '#3b82f6'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-4 font-semibold text-slate-600 rounded-tl-lg">Hạng</th>
                                    <th className="p-4 font-semibold text-slate-600">Nhân viên</th>
                                    <th className="p-4 font-semibold text-slate-600">Bộ phận</th>
                                    <th className="p-4 font-semibold text-slate-600 rounded-tr-lg">Doanh số</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topSalesData.map((row, idx) => (
                                    <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                        <td className="p-4 font-bold text-slate-800">
                                            {idx === 0 ? <span className="text-amber-500 text-xl">🥇 1</span> :
                                                idx === 1 ? <span className="text-slate-400 text-xl">🥈 2</span> :
                                                    idx === 2 ? <span className="text-amber-700 text-xl">🥉 3</span> : `#${idx + 1}`}
                                        </td>
                                        <td className="p-4 font-bold text-slate-800">{row.name}</td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${row.department === 'Telesale' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                {row.department}
                                            </span>
                                        </td>
                                        <td className="p-4 font-bold text-slate-800">{row.revenue} Tr</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );

    const { t } = useTranslation();


    return (
        <Layout>
            <div className="min-h-screen font-sans text-slate-900 selection:bg-blue-100">
                {/* Top Navbar */}
                <div className="max-w-full mx-auto flex flex-col md:flex-row justify-between items-center h-auto mb-4 md:mb-8 md:py-0 gap-4">
                    <div className="flex items-center gap-3">
                        <span className="text-xl font-extrabold tracking-tight text-000000e0">
                            Báo cáo Marketing
                        </span>
                    </div>
                    {/* <DatePicker
                        className="w-full md:w-[220px]"
                        placeholder={t('customer.select_time')}
                        allowClear
                    /> */}
                    <div className="flex flex-wrap gap-3">
                        <DatePicker.RangePicker
                            value={perfRange}
                            onChange={(dates) => { if (dates && dates[0] && dates[1]) setPerfRange([dates[0], dates[1]]); }}
                            allowClear={false}
                            format="DD/MM/YYYY"
                        />

                        {/* Chi nhánh lấy từ API telesale-performance-new (hiện chỉ áp dụng cho tab Telesale) */}
                        <Select
                            placeholder={t('roomchat.select_branch')}
                            className="w-[220px]"
                            allowClear
                            value={tsBranchId}
                            onChange={(v) => setTsBranchId(v)}
                            options={(tsData?.branches || []).map((b) => ({ label: b.name, value: b.id }))}
                            popupClassName="dark:bg-[#1f1f1f]"
                        />
                    </div>
                </div>

                <main className="max-w-full mx-auto">
                    {/* Navigation 6 Tabs */}
                    <div className="sticky top-[70px] z-10 flex overflow-x-auto bg-white rounded-xl shadow-sm border border-slate-200 p-1.5 mb-8 gap-1 scrollbar-hide">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`whitespace-nowrap px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 flex-1 text-center ${activeTab === tab.id
                                    ? 'bg-blue-50 text-blue-700 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Router */}
                    {/* {activeTab === 'overview' && renderOverview()} */}
                    {activeTab === 'page' && renderPage()}
                    {activeTab === 'telesale' && renderTelesale()}
                    {activeTab === 'onsite' && renderOnsite()}
                    {activeTab === 'tech' && renderTech()}
                    {/* {activeTab === 'topsales' && renderTopSales()} */}
                </main>
            </div>
        </Layout>
    );
}